(() => {
  if (window.__vhBoostCartObserver) return;
  window.__vhBoostCartObserver = true;

  const BUTTON_SELECTOR = '.boost-sd__button';
  const CTA_TEXT_SELECTOR =
    '.boost-sd__cta-button-text, .boost-sd__button-text';
  const GRID_SELECTOR =
    '.collection-grid-section.native-boost-fallback-is-hidden';

  const originalFetch = window.fetch.bind(window);
  let lastHandledButton = null;
  let lastHandledAt = 0;

  const getNotification = () =>
    document.querySelector('vh-cart-notification');

  const parseMetadata = (button) => {
    const raw = button?.getAttribute('data-metadata');

    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch (error) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = raw;

      try {
        return JSON.parse(textarea.value);
      } catch (decodeError) {
        console.warn('Unable to parse Boost data-metadata:', raw);
        return null;
      }
    }
  };

  const findValue = (value, keys) => {
    if (!value || typeof value !== 'object') return null;

    for (const key of keys) {
      if (value[key] !== undefined && value[key] !== null) {
        return value[key];
      }
    }

    for (const child of Object.values(value)) {
      if (child && typeof child === 'object') {
        const match = findValue(child, keys);
        if (match !== null) return match;
      }
    }

    return null;
  };

  const normalizeId = (value) => {
    if (value === undefined || value === null) return null;

    const matches = String(value).match(/\d+/g);
    return matches?.length ? matches[matches.length - 1] : null;
  };

  const getVariantId = (button, metadata) => {
    const boostCartVariantId = normalizeId(
      metadata?.action?.addToCart?.productId
    );

    if (boostCartVariantId) return boostCartVariantId;

    const metadataId = normalizeId(
      findValue(metadata, [
        'variantId',
        'variant_id',
        'selectedVariantId',
        'selected_variant_id',
        'variant'
      ])
    );

    if (metadataId) return metadataId;

    const product = button.closest(
      '.boost-sd__product-item, .boost-sd__product-card, [class*="boost-sd__product-item"]'
    );

    const inputId =
      product?.querySelector(
        'input[name="id"], select[name="id"], [data-variant-id]'
      )?.value ||
      product?.querySelector('[data-variant-id]')?.getAttribute(
        'data-variant-id'
      );

    return normalizeId(inputId);
  };

  const getProductHandle = (button, metadata) => {
    const metadataHandle = findValue(metadata, [
      'handle',
      'productHandle',
      'product_handle'
    ]);

    if (metadataHandle) {
      return String(metadataHandle)
        .replace(/^\/products\//, '')
        .split('?')[0]
        .split('#')[0];
    }

    const product = button.closest(
      '.boost-sd__product-item, .boost-sd__product-card, [class*="boost-sd__product-item"]'
    );

    const link = product?.querySelector('a[href*="/products/"]');
    const href = link?.getAttribute('href') || '';

    const match = href.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  };

  const resolveVariantId = async (button, metadata) => {
    const directId = getVariantId(button, metadata);
    if (directId) return directId;

    const handle = getProductHandle(button, metadata);
    if (!handle) return null;

    try {
      const response = await originalFetch(
        `/products/${encodeURIComponent(handle)}.js`,
        {
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        }
      );

      if (!response.ok) return null;

      const product = await response.json();
      const variants = Array.isArray(product?.variants)
        ? product.variants
        : [];

      if (variants.length === 1) {
        return normalizeId(variants[0].id);
      }

      const availableVariants = variants.filter(
        (variant) => variant.available
      );

      if (availableVariants.length === 1) {
        return normalizeId(availableVariants[0].id);
      }

      console.warn(
        'Boost product has multiple variants; cannot safely choose one.',
        { handle, variants }
      );

      return null;
    } catch (error) {
      console.warn(
        'Unable to resolve Boost variant from product JSON.',
        { handle, error }
      );
      return null;
    }
  };

  const getCart = async () => {
    const response = await originalFetch('/cart.js', {
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`Unable to load cart: ${response.status}`);
    }

    return response.json();
  };

  const showNotification = async (addedItem, button) => {
    const cart = await getCart();
    const notification = getNotification();

    if (
      !notification ||
      typeof notification.renderTurboItem !== 'function'
    ) {
      console.warn('Van Horn cart notification is not ready.');
      return;
    }

    const cartItem =
      cart.items?.find(
        (item) =>
          String(item.variant_id || item.id) ===
          String(addedItem.variant_id || addedItem.id)
      ) || addedItem;

    notification.setActiveElement?.(button);
    await notification.refreshCartDrawer?.();
    notification.renderTurboItem(cartItem, cart);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
      });
    });
  };

  const addToCart = async (button) => {
    const metadata = parseMetadata(button);
    const variantId = await resolveVariantId(button, metadata);

    if (!variantId) {
      console.error(
        'Unable to determine Boost variant ID.',
        { button, metadata }
      );
      return;
    }

    const originalButtonHTML = button.innerHTML;

    button.setAttribute('aria-busy', 'true');
    button.setAttribute('disabled', '');
    button.classList.add('boost-sd__button--loading', 'loading', 'vh-boost-loading');
    button.innerHTML = `
      <span class="vh-boost-loading__spinner" aria-hidden="true"></span>
    `;

    try {
      const sellingPlan =
        metadata?.action?.addToCart?.selling_plan || '';

      const payload = {
        id: Number(variantId),
        quantity: 1
      };

      if (sellingPlan) {
        payload.selling_plan = Number(sellingPlan);
      }

      const response = await originalFetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const addedItem = await response.json();

      if (!response.ok) {
        console.error('Boost AJAX add failed:', addedItem);
        return;
      }

      await showNotification(addedItem, button);
    } catch (error) {
      console.error('Boost AJAX add failed:', error);
    } finally {
      button.removeAttribute('aria-busy');
      button.removeAttribute('disabled');
      button.classList.remove(
        'boost-sd__button--loading',
        'loading',
        'vh-boost-loading'
      );
      button.innerHTML = originalButtonHTML;
    }
  };

  const getBoostButton = (event) => {
    if (!(event.target instanceof Element)) return null;

    const button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return null;

    const ctaText = button.querySelector(CTA_TEXT_SELECTOR);
    const normalizedText = ctaText?.textContent
      ?.replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    /*
     * TURBO changes the surrounding markup between grid/list/filter views.
     * The stable CTA signature is the Boost button itself plus its
     * "Add to cart" text child, so do not depend on any parent layout.
     */
    if (normalizedText !== 'add to cart') {
      return null;
    }

    return button;
  };

  const blockBoostNavigation = (event) => {
    const button = getBoostButton(event);
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const now = Date.now();

    if (
      button === lastHandledButton &&
      now - lastHandledAt < 800
    ) {
      return;
    }

    lastHandledButton = button;
    lastHandledAt = now;

    addToCart(button);
  };

  /*
   * Only handle an actual click. Using pointerdown/mousedown caused
   * accidental cart adds when a customer touched an ATC button while
   * swiping/scrolling on mobile. Capture phase still lets us run before
   * Turbo's click handler and prevent its /cart redirect.
   */
  window.addEventListener(
    'click',
    blockBoostNavigation,
    true
  );

  window.addEventListener('beforeunload', () => {
    console.debug(
      'Van Horn page unload:',
      window.location.href
    );
  });
})();
